import { useState, useEffect, useCallback, useRef } from 'react';
import { mediaAPI, IMAGE_BASE } from '../services/api';
import { Search, Square, X } from 'lucide-react';

export default function MediaPickerModal({ open, onClose, onSelect, multiple = true }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [selected, setSelected] = useState([]);
    const searchTimeout = useRef(null);

    const fetchMedia = useCallback(async () => {
        setLoading(true);
        try {
            const res = await mediaAPI.getAll({ page, limit: 50, search: search || undefined });
            setItems(res.data.items || []);
            setTotalPages(res.data.totalPages || 1);
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [page, search]);

    useEffect(() => {
        if (open) {
            fetchMedia();
        }
    }, [open, fetchMedia]);

    useEffect(() => {
        if (open) {
            setSelected([]);
            setSearchInput('');
            setSearch('');
            setPage(1);
        }
    }, [open]);

    const handleSearchChange = (val) => {
        setSearchInput(val);
        clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            setSearch(val);
            setPage(1);
        }, 400);
    };

    const toggleSelect = (item) => {
        if (multiple) {
            setSelected(prev => {
                const idx = prev.indexOf(item.id);
                if (idx !== -1) return prev.filter(id => id !== item.id);
                return [...prev, item.id];
            });
        } else {
            setSelected(prev => {
                if (prev.includes(item.id)) return [];
                return [item.id];
            });
        }
    };

    const handleConfirm = () => {
        const selectedItems = selected.map(id => items.find(i => i.id === id)).filter(Boolean);
        onSelect(selectedItems);
        onClose();
    };

    if (!open) return null;

    return (
        <div
            className="modal-overlay"
            style={{ zIndex: 1001 }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="modal" style={{ maxWidth: 800, width: '95%' }}>
                <div className="modal-header">
                    <h2>Görsel Havuzundan Seç</h2>
                    <button className="modal-close" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                    {/* Search */}
                    <div style={{ position: 'relative', marginBottom: '1rem' }}>
                        <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input
                            className="form-control"
                            placeholder="Dosya adı ile ara..."
                            value={searchInput}
                            onChange={e => handleSearchChange(e.target.value)}
                            style={{ paddingLeft: 32 }}
                        />
                    </div>

                    {loading ? (
                        <div className="loading"><div className="spinner"></div></div>
                    ) : items.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                            Görsel bulunamadı
                        </div>
                    ) : (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                            gap: '0.5rem'
                        }}>
                            {items.map(item => (
                                <div
                                    key={item.id}
                                    onClick={() => toggleSelect(item)}
                                    style={{
                                        position: 'relative',
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        border: selected.includes(item.id) ? '2px solid #6c5ce7' : '2px solid var(--border)',
                                        cursor: 'pointer',
                                        transition: 'border-color 0.15s'
                                    }}
                                >
                                    <div style={{ position: 'relative', paddingTop: '100%' }}>
                                        <img
                                            src={IMAGE_BASE + item.url}
                                            alt={item.filename}
                                            style={{
                                                position: 'absolute',
                                                top: 0, left: 0, width: '100%', height: '100%',
                                                objectFit: 'cover'
                                            }}
                                            loading="lazy"
                                        />
                                        {selected.includes(item.id) ? (
                                            <div style={{
                                                position: 'absolute', top: 4, left: 4,
                                                background: '#6c5ce7',
                                                borderRadius: '50%', width: 24, height: 24,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: '#fff', fontSize: '0.75rem', fontWeight: 700,
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                            }}>
                                                {selected.indexOf(item.id) + 1}
                                            </div>
                                        ) : (
                                            <div style={{
                                                position: 'absolute', top: 4, left: 4,
                                                background: 'rgba(255,255,255,0.85)',
                                                borderRadius: 4, padding: 2,
                                                color: 'var(--text-muted)'
                                            }}>
                                                <Square size={16} />
                                            </div>
                                        )}
                                    </div>
                                    <div style={{
                                        padding: '4px 6px', fontSize: '0.7rem',
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                    }}>
                                        {item.filename}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                            <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ fontSize: '0.8rem' }}>
                                Önceki
                            </button>
                            <span style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                {page} / {totalPages}
                            </span>
                            <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ fontSize: '0.8rem' }}>
                                Sonraki
                            </button>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>İptal</button>
                    <button
                        className="btn btn-primary"
                        disabled={selected.length === 0}
                        onClick={handleConfirm}
                    >
                        {selected.length > 0 ? `${selected.length} Görsel Seç` : 'Seç'}
                    </button>
                </div>
            </div>
        </div>
    );
}
